'use client';

import { ComponentProps } from 'react';

import { Button, RichText } from '@/ui';
import {
  IconBrandFacebookFilled,
  IconBrandGoogleFilled,
  IconBrandInstagram,
  IconBrandTiktokFilled,
  IconBrandTripadvisor,
  IconBrandXFilled,
  IconBrandYoutubeFilled,
} from '@tabler/icons-react';
import { RTEContent, SocialWithContact } from '@types';
import clsx from 'clsx';

import { IconBrandDesignMyNight } from './social-icons/custom-icons/icon-brand-design-my-night';

export interface LeaveFeedbackProps extends React.ComponentPropsWithoutRef<'ul'> {
  links: SocialWithContact;
  intro: RTEContent;
  componentProps?: {
    button?: ComponentProps<typeof Button>;
  };
}

export function LeaveFeedback({ links, className, componentProps, intro }: LeaveFeedbackProps) {
  const socialIconMap = [
    {
      name: 'Facebook',
      Icon: IconBrandFacebookFilled,
      link: links?.facebook,
      background: 'bg-blue-600 hover:bg-blue-700',
      foreground: 'text-blue-50 hover:text-blue-100',
    },
    {
      name: 'X',
      Icon: IconBrandXFilled,
      link: links?.twitter,
      background: 'bg-gray-800 hover:bg-gray-900',
      foreground: 'text-gray-50 hover:text-gray-100',
    },
    {
      name: 'Instagram',
      Icon: IconBrandInstagram,
      link: links?.instagram,
      background: 'bg-pink-600 hover:bg-pink-700',
      foreground: 'text-pink-50 hover:text-pink-100',
    },
    {
      name: 'YouTube',
      Icon: IconBrandYoutubeFilled,
      link: links?.youTube,
      background: 'bg-red-600 hover:bg-red-700',
      foreground: 'text-red-50 hover:text-red-100',
    },
    {
      name: 'TikTok',
      Icon: IconBrandTiktokFilled,
      link: links?.tikTok,
      background: 'bg-purple-800 hover:bg-purple-900',
      foreground: 'text-purple-50 hover:text-purple-100',
    },
    {
      name: 'Google',
      Icon: IconBrandGoogleFilled,
      link: links?.googlePlace,
      background: 'bg-green-600 hover:bg-green-700',
      foreground: 'text-green-50 hover:text-green-100',
    },
    {
      name: 'TripAdvisor',
      Icon: IconBrandTripadvisor,
      link: links?.tripadvisor,
      background: 'bg-emerald-600 hover:bg-emerald-700',
      foreground: 'text-emerald-50 hover:text-emerald-100',
    },
    {
      name: 'DesignMyNight',
      Icon: IconBrandDesignMyNight,
      link: links?.designMyNight,
      background: 'bg-cyan-600 hover:bg-cyan-700',
      foreground: 'text-cyan-50 hover:text-cyan-100',
    },
  ];

  return (
    <div className="flex flex-col items-center gap-8 text-center">
      <RichText>{intro.content}</RichText>
      <ul className={clsx('flex w-full min-w-80 flex-wrap justify-center gap-3', className)}>
        {socialIconMap
          ?.filter((i) => Boolean(i.link?.trim()))
          ?.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
          ?.map(({ name, Icon, link, background, foreground }) => (
            <Button
              key={name}
              href={link?.trim()}
              aria-label={name}
              variant="ghost"
              size={'lg'}
              target={link?.includes('mailto:') || link?.includes('tel:') ? '_self' : '_blank'}
              {...componentProps?.button}
              className={clsx('gap-3 rounded-full px-6', componentProps?.button?.className, background, foreground)}
            >
              <Icon className="shrink-0" />
              {name}
            </Button>
          ))}
      </ul>
    </div>
  );
}
